import React, { useState, useRef, useEffect } from "react";

/**
 * SearchableSelect Component
 * 
 * A custom dropdown component that allows filtering options via a text input.
 * Supports selecting a value from a filtered list of options.
 * 
 * Props:
 * - options: Array of strings or objects {value, label} (normalized to objects internally)
 * - value: Current selected value
 * - onChange: Callback function(value)
 * - placeholder: Placeholder text for the input
 * - label: Label for the field (optional)
 * - name: Name of the field (for the onChange event mock)
 */
const SearchableSelect = ({ options = [], value, onChange, placeholder, label, name, customOptionStyle = {} }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const wrapperRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [wrapperRef]);

    // Update search term when value changes or when opening (reset/sync)
    useEffect(() => {
        if (!isOpen) {
            // Find label for the current value to display in the input
            const normalized = normalizeOptions();
            const selectedOpt = normalized.find(opt => String(opt.value) === String(value));
            if (selectedOpt) {
                setSearchTerm(selectedOpt.label);
            } else if (!value) {
                setSearchTerm("");
            }
            // If value exists but not in options, we might keep it or show generic. 
            // Often options are loaded dynamically, so it might be tricky. 
            // But for now, if match is selected, we want title.
        }
    }, [value, isOpen, options]); // Added options to dep

    // Normalize options to { value, label }
    const normalizeOptions = () => {
        return options.map(opt => {
            if (typeof opt === 'object' && opt !== null) {
                return opt;
            }
            return { value: opt, label: opt };
        });
    };

    const normalizedOptions = normalizeOptions();

    // Filter options
    const filteredOptions = normalizedOptions.filter(opt =>
        String(opt.label).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (selectedValue) => {
        const selectedOpt = normalizedOptions.find(opt => opt.value === selectedValue);

        onChange({ target: { name, value: selectedValue } });
        setIsOpen(false);
        if (selectedOpt) {
            setSearchTerm(selectedOpt.label); // Set input to Label
        }
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setIsOpen(true);
        // If user clears input, maybe clear selection? 
        // For now, we only change parent state on click, or we could support clearing.
        if (e.target.value === "") {
            onChange({ target: { name, value: "" } });
        }
    };

    return (
        <div ref={wrapperRef} className="searchable-select-container">
            <style>{`
                @keyframes dropdownSlide {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .searchable-input {
                    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                    border: 2px solid #e9ecef;
                    color: #2c3e50;
                    border-radius: 10px;
                    padding: 10px 40px 10px 16px;
                    width: 100%;
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.3s ease;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                }
                .searchable-input:hover {
                    border-color: #667eea;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
                }
                .searchable-input:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                    background: linear-gradient(135deg, #ffffff 0%, #f0f4ff 100%);
                }
                .searchable-input::placeholder {
                    color: #adb5bd;
                    font-weight: 400;
                }
                .dropdown-menu-modern {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    z-index: 1000;
                    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                    border: 2px solid #e9ecef;
                    border-radius: 12px;
                    margin-top: 8px;
                    max-height: 280px;
                    overflow-y: auto;
                    box-shadow: 0 12px 32px rgba(0,0,0,0.15);
                    animation: dropdownSlide 0.3s ease-out;
                }
                .dropdown-menu-modern::-webkit-scrollbar {
                    width: 8px;
                }
                .dropdown-menu-modern::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .dropdown-menu-modern::-webkit-scrollbar-thumb {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 10px;
                }
                .dropdown-menu-modern::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(135deg, #5568d3 0%, #63408a 100%);
                }
                .dropdown-option {
                    padding: 12px 16px;
                    cursor: pointer;
                    color: #2c3e50;
                    border-bottom: 1px solid rgba(0,0,0,0.05);
                    font-size: 14px;
                    font-weight: 500;
                    transition: all 0.2s ease;
                    position: relative;
                }
                .dropdown-option:last-child {
                    border-bottom: none;
                }
                .dropdown-option:hover {
                    background: linear-gradient(135deg, #e7f3ff 0%, #d0e7ff 100%);
                    padding-left: 20px;
                    color: #667eea;
                }
                .dropdown-option.selected {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    font-weight: 600;
                }
                .dropdown-option.selected:hover {
                    background: linear-gradient(135deg, #5568d3 0%, #63408a 100%);
                }
                .dropdown-empty {
                    padding: 16px;
                    color: #adb5bd;
                    font-size: 14px;
                    text-align: center;
                    font-style: italic;
                }
                .search-icon {
                    position: absolute;
                    right: 16px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #adb5bd;
                    font-size: 14px;
                    pointer-events: none;
                    transition: all 0.3s ease;
                }
                .searchable-select-container:hover .search-icon {
                    color: #667eea;
                }
            `}</style>
            {label && <label style={{ color: "#6c757d", fontSize: "13px", marginBottom: "6px", display: "block", fontWeight: "600" }}>{label}</label>}

            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={() => { setSearchTerm(""); setIsOpen(true); }}
                    placeholder={placeholder}
                    className="searchable-input"
                />

                {/* Clear Button */}
                {(searchTerm || value) && (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setSearchTerm("");
                            onChange({ target: { name, value: "" } });
                        }}
                        style={{
                            position: "absolute",
                            right: "40px", // Left of the chevron
                            top: "50%",
                            transform: "translateY(-50%)",
                            cursor: "pointer",
                            color: "#adb5bd",
                            padding: "5px",
                            zIndex: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                        onMouseEnter={(e) => e.target.style.color = "#dc3545"}
                        onMouseLeave={(e) => e.target.style.color = "#adb5bd"}
                        title="Temizle"
                    >
                        <i className="fas fa-times" style={{ fontSize: "14px", pointerEvents: "none" }}></i>
                    </div>
                )}

                {isOpen && (
                    <div className="dropdown-menu-modern">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleSelect(opt.value)}
                                    className={`dropdown-option ${value === opt.value ? 'selected' : ''}`}
                                    style={customOptionStyle} // Apply custom styles here
                                >
                                    {opt.label}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-empty">
                                🔍 Sonuç bulunamadı
                            </div>
                        )}
                    </div>
                )}
                <i className="fas fa-chevron-down search-icon"></i>
            </div>
        </div>
    );
};

export default SearchableSelect;
